import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { TbEdit } from "react-icons/tb";
import { motion } from 'framer-motion';
import '../../../assets/css/language.css'
const AddLanguage = ({ pageTitle }) => {
  const navigate = useNavigate();

  const [languages, setLanguages] = useState([]);
  const [languageName, setName] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("active");
  const [message, setMessage] = useState("");
  const [editingLanguage, setEditingLanguage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get("/language/view");
        setLanguages(response.data);
      } catch (error) {
        console.error("Error fetching languages:", error);
      }
    };
    fetchLanguages();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const dataToSend = { languageName, code, status };

    try {
      const response = await axios.post("/language/add", dataToSend);
      const newLanguage = response.data.language;
      setLanguages((prev) => [...prev, newLanguage]);
      setMessage("Language added successfully!");
      setName("");
      setCode("");
      setStatus("active");
    } catch (error) {
      console.error(error);
      setMessage(error.response?.data?.message || "An error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setIsSubmitting(true);
    try {
      await axios.delete(`/language/delete/${id}`);
      setLanguages((prev) => prev.filter((language) => language.id !== id));
      setMessage("Language deleted successfully!");
    } catch (error) {
      console.error("Error deleting language:", error);
      setMessage("Could not delete language.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (language) => {
    setEditingLanguage(language);
    setShowModal(true);
  };

  const handleUpdateStatus = async () => {
    setIsSubmitting(true);
    try {
      await axios.put(`/language/update/${editingLanguage._id}`, {
        status: editingLanguage.status,
      });
      setLanguages((prev) =>
        prev.map((lang) =>
          lang._id === editingLanguage._id ? { ...lang, status: editingLanguage.status } : lang
        )
      );
      setShowModal(false);
      setMessage("Language status updated successfully!");
    } catch (error) {
      console.error("Error updating status:", error);
      setMessage("Could not update language status.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-creative-container">
      {/* Header Section */}
      <div className="header-section">
        <motion.button 
          className="back-button"
          onClick={() => navigate(-1)}
          whileHover={{ scale: 1.05 }}
        >
          <IoArrowBack /> Back
        </motion.button>
        <h1 className="page-title">{pageTitle}</h1>
      </div>

      {/* Error/Success Message */}
      {message && (
        <div className={`message ${message.includes("success") ? "success-message" : "error-message"}`}>
          {message}
        </div>
      )}

      <div className="languages-container">
        {/* Languages Grid */}
        <div className="languages-grid">
          {languages.map((language) => (
            <motion.div 
              key={language._id} 
              className={`language-card ${language.status === "disabled" ? "disabled" : ""}`}
              whileHover={{ scale: 1.02 }}
            >
              <h3 className="language-name">{language.languageName}</h3>
              <p className="language-code">Code: {language.code}</p>
              <div className="language-status">
                Status: <span className={`status-badge ${language.status}`}>{language.status}</span>
              </div>
              <motion.button
                className="language-modify-btn"
                onClick={() => handleEdit(language)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Edit"
              >
                <TbEdit />
              </motion.button>
            </motion.div>
          ))}
        </div>

        {/* Add Language Form */}
        <div className="add-language-form">
          <h2 className="form-title">Add New Language</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="input-label">Language Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter language name"
                value={languageName}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="input-label">Language Code</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter language code (e.g., en)"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="input-label">Status</label>
              <div className="language-selector">
                <motion.div
                  className={`language-option ${status === "active" ? "selected" : ""}`}
                  onClick={() => setStatus("active")}
                  whileHover={{ scale: 1.02 }}
                >
                  Active
                </motion.div>
                <motion.div
                  className={`language-option ${status === "disabled" ? "selected" : ""}`}
                  onClick={() => setStatus("disabled")}
                  whileHover={{ scale: 1.02 }}
                >
                  Disabled
                </motion.div>
              </div>
            </div>

            <div className="form-actions">
              <motion.button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {isSubmitting ? (
                  <>
                    <span className="button-spinner"></span>
                    Adding...
                  </>
                ) : (
                  'Add Language'
                )}
              </motion.button>
            </div>
          </form>
        </div>
      </div>

      {/* Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Language Status</h2>
              <button 
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="input-label">Status</label>
                <div className="language-selector">
                  <motion.div
                    className={`language-option ${editingLanguage?.status === "active" ? "selected" : ""}`}
                    onClick={() => setEditingLanguage((prev) => ({ ...prev, status: "active" }))}
                    whileHover={{ scale: 1.02 }}
                  >
                    Active
                  </motion.div>
                  <motion.div
                    className={`language-option ${editingLanguage?.status === "disabled" ? "selected" : ""}`}
                    onClick={() => setEditingLanguage((prev) => ({ ...prev, status: "disabled" }))}
                    whileHover={{ scale: 1.02 }}
                  >
                    Disabled
                  </motion.div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <motion.button
                className="cancel-button"
                onClick={() => setShowModal(false)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Cancel
              </motion.button>
              <motion.button
                className="save-button"
                onClick={handleUpdateStatus}
                disabled={isSubmitting}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {isSubmitting ? (
                  <>
                    <span className="button-spinner"></span>
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </motion.button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

    </div>
  );
};

export default AddLanguage;